import { Stack } from '@mui/joy';
import { t } from 'i18next';
import { get } from 'lodash';
import { forwardRef, useMemo } from 'react';

import { LISTBOX_STYLE } from 'contacts/constants';
import { ContactGroupType } from 'contacts/enums';
import { contactGroupService } from 'contacts/services';
import {
  IContactGroup,
  IContactGroupAddress,
  IContactGroupAddressListRequestPayload,
} from 'contacts/types';
import { FormField, IAutocompleteProps } from 'core/components';
import { DEFAULT_PLACEHOLDER, NON_EMPTY } from 'core/constants';
import { LabeledValue, NeutralChip, TextHighlighter } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { Direction, Level } from 'theme/enums';

interface IVendorLocationFormFieldProps extends IAutocompleteProps {
  contactGroup?: Partial<IContactGroup>;
  groupOptionsByContactGroup?: boolean;
  requestPayload?: IContactGroupAddressListRequestPayload;
  hidden?: boolean;
}

const getDefaultLocationLabel = (option: IAutocompleteOption) => {
  const item = option as unknown as IContactGroupAddress;

  if (!item?.name) {
    return '';
  }

  const label = [item.name];

  if (item?.accountsPayableId) {
    label.push(`[${item.accountsPayableId}]`);
  }

  return label.join(' ');
};

const getDefaultQueryFromValue = (label: string) => {
  if (!label) {
    return '';
  }

  const bracketIndex = label.indexOf('[');

  return bracketIndex !== -1 ? label.substring(0, bracketIndex).trim() : label.trim();
};

export const VendorLocationFormField = forwardRef(function VendorLocationFormField(
  {
    name = 'vendorLocation',
    label = t('labelVendorLocation'),
    extraLabel = null,
    placeholder,
    contactGroup,
    requestPayload: customRequestPayload = {},
    getLabel = getDefaultLocationLabel,
    getQueryFromValue = getDefaultQueryFromValue,
    ...rest
  }: IVendorLocationFormFieldProps,
  ref
) {
  const { control, errors } = useFormContext();

  const requestPayload = useMemo(() => {
    return {
      ...customRequestPayload,
      filters: {
        ...(customRequestPayload?.filters as object),
        contactGroupType: ContactGroupType.VENDOR,
        accountsPayableId: NON_EMPTY,
      },
    };
  }, [customRequestPayload]);

  const renderOptions = (
    option: IContactGroupAddress,
    _: (option: IContactGroupAddress) => string,
    inputValue?: string
  ) => {
    return (
      <Stack sx={{ width: '100%' }}>
        {option.id && (
          <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <TextHighlighter
              level={Level.TITLE_MD}
              text={option.name || DEFAULT_PLACEHOLDER}
              highlight={inputValue}
            />

            {contactGroup?.id !== option?.contactGroup?.id && option?.contactGroup?.name && (
              <NeutralChip>
                <TextHighlighter
                  level={Level.TITLE_SM}
                  text={option.contactGroup?.name}
                  highlight={inputValue}
                />
              </NeutralChip>
            )}
          </Stack>
        )}

        <LabeledValue
          label={t('labelSupplierNumber')}
          value={
            <TextHighlighter
              level={Level.BODY_SM}
              text={option.contactGroup?.alternateVendorId || DEFAULT_PLACEHOLDER}
              highlight={inputValue}
            />
          }
          options={{ labelLevel: Level.BODY_SM, labelSx: { fontWeight: 400 }, inline: true }}
        />

        <LabeledValue
          label={t('labelAPNumber')}
          value={
            <TextHighlighter
              level={Level.BODY_SM}
              text={option.accountsPayableId || DEFAULT_PLACEHOLDER}
              highlight={inputValue}
            />
          }
          options={{ labelLevel: Level.BODY_SM, labelSx: { fontWeight: 400 }, inline: true }}
        />

        <LabeledValue
          label={t('labelVendorNumbers')}
          value={
            <TextHighlighter
              level={Level.BODY_SM}
              text={option.contactGroup?.legacySupplierNumbers?.join(', ') || DEFAULT_PLACEHOLDER}
              highlight={inputValue}
            />
          }
          options={{ labelLevel: Level.BODY_SM, labelSx: { fontWeight: 400 }, inline: true }}
        />

        <LabeledValue
          label={t('labelLegacyVendorNumbers')}
          value={
            <TextHighlighter
              level={Level.BODY_SM}
              text={option.contactGroup?.legacyVendorNumbers?.join(', ') || DEFAULT_PLACEHOLDER}
              highlight={inputValue}
            />
          }
          options={{ labelLevel: Level.BODY_SM, labelSx: { fontWeight: 400 }, inline: true }}
        />
      </Stack>
    );
  };

  return (
    <>
      <FormField
        ref={ref}
        name={name}
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          control,
          error: get(errors, name),
          label,
          extraLabel,
          placeholder,
          renderOptions,
          getOptions: contactGroupService.getContactGroupAddresses,
          getLabel,
          requestPayload,
          includeFilterQuery: true,
          listboxStyle: LISTBOX_STYLE,
          getQueryFromValue,
          ...rest,
        }}
      />
    </>
  );
});
